/**
 * DevourTopNav — global authenticated top navigation bar.
 *
 * Hosts the brand mark, the section nav, one fixed-width live slot
 * (TopNavLiveSlot — highest-priority transient state only), Fuel balance,
 * the alerts bell, a count-gated cart, and the avatar (XP ring + level
 * plate). Everything the bar used to carry as loose chips lives in the
 * avatar's player panel; see DevourTopNavUserData.
 */

import { ReactElement, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import { IStore } from "@/redux/defaultStore";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useGetDropInReady } from "@/hooks/useGetDropInVisible";
import { useGetUserLevel } from "@/hooks/useGetUserLevel";
import { useMyStreaks } from "@/hooks/useStreaks";
import { useGetAsset } from "@/hooks/useAsset";
import { useFuelUpGameState } from "@/hooks/fuelup/useFuelUpGameState";
import { useGetSeasonPasses } from "@/hooks/useSeasonPasses";
import { useGetFuelBalance, formatFuelUsd } from "@/hooks/useGetFuelBalance";
import { useMenuOrder } from "@/hooks/menuOrder/useMenuOrder";
import { DEVOUR_MERCHANT_ID } from "@/constants/merchantConstants";
import { useIsMobile } from "@/hooks/play/useIsMobile";
import useGetGuildOwnerships from "@/hooks/useGetGuildOwnerships";
import useFilteredOverwolfGameInfo from "@/hooks/useFilteredOverwolfGameInfo";
import DevourTopNavUserData from "./DevourTopNavUserData";
import { TopNavLiveSlot } from "./TopNavLiveSlot";
import SignUpToast from "./SignUpToast";
import LevelRewardsDrawer from "@/components/drawers/LevelRewardsDrawer";
import LeaderboardDrawer from "@/components/drawers/LeaderboardDrawer";
import LoadFuelModal from "@/components/modals/LoadFuelModal";
import LoginModal from "@/components/modals/LoginModal";
import MyActivityDrawer from "@/components/drawers/MyActivityDrawer";
import GoVipRewardsInventoryDrawer from "@/components/goVip/GoVipRewardsInventoryDrawer";
import DevourPlayIcon from "@/pngs/devourplay-icon.png";
import { FaChartLine, FaRegLightbulb, FaBell, FaShoppingCart } from "react-icons/fa";
import { useSocialDrawer, useNotifications, useFriends, useDmConversations, useSocialDrawerVisibility } from "@/components/social/hooks";
import { useGetFeedUnreadCount } from "@/hooks/useGetFeedUnreadCount";

interface Props {
    logoUrl?: string;
    text?: string;
    /** Hide the data stats (Level, XP, Fuel) - useful for non-gaming pages */
    hideStats?: boolean;
    /** Hide the user's profile image in the navbar (prevents duplication with hero profile pics) */
    hideUserImage?: boolean;
}

/**
 * Get page title based on current route
 */
function getPageTitleFromRoute(pathname: string, search: string): string {
    // Check URL params first (for /play?view=X routes)
    const params = new URLSearchParams(search);
    const view = params.get("view");

    if (view) {
        switch (view) {
            case "tournaments":
            case "tournament":
                return "Tournaments";
            case "guilds":
                return "Guilds";
            case "quests":
                return "Quests";
            case "games":
                return "Games";
            case "my-activity":
                return "My Activity";
            case "category":
                return params.get("name") || "Games";
            default:
                return "Play";
        }
    }

    // Check pathname for standalone routes
    if (pathname.startsWith("/competitions")) {
        return "Competitions";
    }
    if (pathname.startsWith("/tournaments")) {
        return "Tournaments";
    }
    if (pathname.startsWith("/ladders")) {
        return "Ladders";
    }
    if (pathname.startsWith("/guilds")) {
        return "Guilds";
    }
    if (pathname.startsWith("/challenges") || pathname.startsWith("/challenge")) {
        return "Challenges";
    }
    if (pathname.startsWith("/leaderboards")) {
        return "Leaderboards";
    }

    return "Play";
}

function DevourTopNav(props: Props): ReactElement {
    const navigate = useNavigate();
    const location = useLocation();
    const [showToast, setShowToast] = useState<boolean>(false);
    const [showLevelsModal, setShowLevelsModal] = useState(false);
    const [showLeaderboardDrawer, setShowLeaderboardDrawer] = useState(false);
    const [showLoadFuelModal, setShowLoadFuelModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showActivityDrawer, setShowActivityDrawer] = useState(false);
    const [showRewardsDrawer, setShowRewardsDrawer] = useState(false);
    /*
     * The brand mark means home, at every width. It used to mean "open the
     * menu" below 900px, which is how navigation ended up behind an
     * unlabelled control. The dock owns navigation there now.
     */
    function handleBrandClick(): void {
        navigate("/play");
    }

    // Redux state
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    /*
     * Electron shell vs. Overwolf iframe. The codebase historically conflates
     * both into `isOnOverwolf`, but Electron has different layout constraints
     * — notably it WANTS the DP brand mark + page title in the navbar (mobile/
     * compact mode) where the Overwolf iframe deliberately hides them to keep
     * its sidebar real estate.
     */
    const isElectron = typeof window !== "undefined" && !!(window as any).electronAPI;
    /*
     * In compact width (~380px in-game panel or any mobile viewport ≤768px),
     * the full game-status chip pushes the avatar off-screen. Render the chip
     * as just a colored status dot when mobile, full chip otherwise.
     */
    const isMobile = useIsMobile();

    // Social Drawer + Combined Unread Count
    const { openDrawer } = useSocialDrawer();
    const { unreadCount: notificationUnreadCount } = useNotifications();
    const { pendingCount: friendRequestCount } = useFriends();
    const { unreadCount: messageUnreadCount } = useDmConversations();
    const { hasAnyEnabledFeature: showSocialDrawerBell } = useSocialDrawerVisibility();
    /*
     * The Feed tab carries its own badge in the drawer (SocialDrawerTabs.tsx)
     * but was missing from this sum, so a user with unread feed items and
     * nothing else saw a clean bar and a badged tab behind it. Cache read —
     * GlobalWebsocketManager writes the value, so this costs no request.
     */
    const feedUnreadCount = useGetFeedUnreadCount();

    // Combined unread count for the bell badge
    const totalUnreadCount = (notificationUnreadCount || 0) + (friendRequestCount || 0) + (messageUnreadCount || 0) + (feedUnreadCount || 0);

    // Fetch user data
    const { data: userProfile } = useGetUserProfile(fullToken);
    const { data: accountLevel } = useGetUserLevel(fullToken, userProfile?.user?.id);
    const { data: transactionsData } = useGetFuelBalance(fullToken, userProfile?.user?.id);
    const { data: guildOwnerships } = useGetGuildOwnerships(fullToken, !!fullToken);
    /*
     * Only pull "my" streaks (not available-to-start), and don't refetch on
     * window focus — the topbar reads streak state ambiently for the LVL
     * chip flame ring, and a 60s stale window is plenty.
     */
    const { data: myStreaksResponse } = useMyStreaks(fullToken, false);

    /*
     * Slot P1 — the game being tracked right now (Overwolf only): the one
     * state that reflects what the player is doing at this moment.
     */
    const owGameInfo = useFilteredOverwolfGameInfo();
    const trackingState = isOnOverwolf && owGameInfo?.isGameRunning && owGameInfo.gameTitle
        ? { name: owGameInfo.gameTitle }
        : null;

    /*
     * Slot P4 — a streak at risk: ACTIVE, this window's requirement NOT met,
     * and the window closes within 6 hours. The record carries the window
     * bookkeeping (currentWindowMet / currentWindowEnd), so this is a read,
     * not a guess.
     */
    const streakAtRiskState = useMemo(() => {
        const now = Date.now();
        let best: { days: number; endsAt: number } | null = null;
        for (const entry of myStreaksResponse?.streaks ?? []) {
            const st = entry.streak;
            if (!st) continue;
            if (String(st.status ?? "") !== "ACTIVE") continue;
            if (st.currentWindowMet !== false) continue;
            const end = st.currentWindowEnd ?? st.aliveUntil;
            if (!end || end <= now || end - now > 6 * 60 * 60 * 1000) continue;
            if (!best || end < best.endsAt) best = { days: st.currentStreak ?? 0, endsAt: end };
        }
        return best;
    }, [myStreaksResponse]);

    /*
     * Longest active streak the user has alive — drives the ambient flame
     * ring around the LVL chip. MyStreakEntry wraps the actual UserStreak
     * record under `entry.streak`, so currentStreak lives at
     * entry.streak.currentStreak (not directly on entry). Empty/zero = no
     * flame.
     */
    const aliveStreakCount = useMemo(() => {
        const entries = myStreaksResponse?.streaks ?? [];
        let best = 0;
        for (const entry of entries) {
            const streak = entry.streak;
            if (!streak) continue;
            const status = String(streak.status ?? "");
            const count = streak.currentStreak ?? 0;
            if (status === "ACTIVE" && count > best) best = count;
        }
        return best;
    }, [myStreaksResponse]);

    /*
     * Fuel-Up Adventure board game state — drives the "Continue Adventure"
     * topbar chip. Reads completed loops + current position so the chip
     * surfaces a one-tap resume action ("Loop 3 · Space 14"). Hook is
     * disabled when there's no auth token; data is null in that case.
     */
    const { data: fuelUpState } = useFuelUpGameState();
    /*
     * availableMoves and dollarsPerMove come back on the same response, so
     * the slot can say "3 moves ready · MOVE" — the board's own verb and its
     * own gate (GameBoard.tsx:145-152) — without a second request. With no
     * moves banked there is nothing to act on, so the run carries the spend
     * line instead and drops out of slot contention.
     */
    const fuelUpResume = useMemo(() => {
        const gs = fuelUpState?.gameState;
        if (!gs) return null;
        const perMove = fuelUpState?.dollarsPerMove ?? 0;
        const remainder = gs.spendRemainder ?? 0;
        const owed = Math.max(0, perMove - remainder);
        return {
            /* A down board still holds the vault, so the entry stays reachable. */
            isActive: Boolean(gs.isActive),
            loop: (gs.completedLoops ?? 0) + 1,
            moves: gs.availableMoves ?? 0,
            toNextMove: perMove > 0 ? `$${owed.toFixed(2)} to next move` : undefined,
        };
    }, [fuelUpState]);

    /*
     * Active Season Pass — drives the persistent thin ribbon above the
     * topbar. Picks the first ACTIVE pass whose date window covers now.
     * The ribbon is dismissable per-session via local state so users who
     * actively don't want to see it can hide it; auto-restored on the
     * next session if the pass is still active.
     */
    const { data: seasonPassesResponse } = useGetSeasonPasses(fullToken);
    const activeSeasonPass = useMemo(() => {
        const list = seasonPassesResponse?.seasonPasses ?? [];
        const now = Date.now();
        /*
         * Defensive epoch coercion: the generated client types startDate /
         * endDate as `number` but the backend may serialize as ISO strings
         * depending on the swagger schema definition. Without this guard,
         * ISO comparisons against a numeric `now` short-circuit to false
         * (`"2026-01-01" <= 1717632000000` → NaN coercion → false) and the
         * ribbon never appears even when a pass is active.
         */
        const toEpoch = (v: unknown): number => {
            if (typeof v === "number") return v;
            if (typeof v === "string") {
                const parsed = new Date(v).getTime();
                return Number.isNaN(parsed) ? 0 : parsed;
            }
            return 0;
        };
        return list.find(p => {
            if (String(p.status ?? "") !== "ACTIVE") return false;
            const start = toEpoch(p.startDate);
            const end = toEpoch(p.endDate);
            return start > 0 && end > 0 && start <= now && now <= end;
        }) ?? null;
    }, [seasonPassesResponse]);
    const seasonRibbon = useMemo(() => {
        if (!activeSeasonPass) return null;
        const now = Date.now();
        const weekMs = 7 * 24 * 60 * 60 * 1000;
        /* Same epoch coercion as activeSeasonPass selector — handles ISO strings. */
        const toEpoch = (v: unknown): number => {
            if (typeof v === "number") return v;
            if (typeof v === "string") {
                const parsed = new Date(v).getTime();
                return Number.isNaN(parsed) ? 0 : parsed;
            }
            return 0;
        };
        const startEpoch = toEpoch(activeSeasonPass.startDate);
        const endEpoch = toEpoch(activeSeasonPass.endDate);
        const totalWeeks = Math.max(1, Math.ceil((endEpoch - startEpoch) / weekMs));
        const currentWeek = Math.min(totalWeeks, Math.floor((now - startEpoch) / weekMs) + 1);
        return {name: activeSeasonPass.name, currentWeek, totalWeeks};
    }, [activeSeasonPass]);

    // Get user's current guild
    const currentGuild = useMemo(() => {
        if (!guildOwnerships?.membership?.guild || !guildOwnerships?.qualifiedGuilds) return null;
        return guildOwnerships.qualifiedGuilds.find(guild => guild.id === guildOwnerships.membership?.guild) || null;
    }, [guildOwnerships]);

    // Get guild's leaderboard rank (monthlyRank or rank)
    const guildRank = currentGuild?.monthlyRank || currentGuild?.rank || null;
    /* guild.icon is an ASSET ID, not a URL — resolve it like the guild pages do. */
    const { data: guildIconAsset } = useGetAsset(currentGuild?.icon);

    // Get user's rank within the guild
    const userRankInGuild = (guildOwnerships?.membership as { rank?: number })?.rank || null;


    // Calculate level and XP values
    const level = accountLevel?.level || userProfile?.user?.level || 1;
    const experienceGained = accountLevel?.experienceGainedAtThisLevel || 0;
    const experienceRequired = accountLevel?.experienceRequiredForNextLevel || 1;
    const totalForLevel = experienceGained + experienceRequired;

    // Format fuel balance — mobile drops the cents so the chip fits.
    /*
     * Drop the cents once the balance hits triple digits (or on mobile) — the
     * ".70" tail on a large balance pushes the navbar off-screen in the narrow
     * Electron desktop window. Small balances keep cents so they read precisely.
     */
    const fuelShowCents = !isMobile && (transactionsData?.balanceInUsd ?? 0) < 100;
    // Canonical formatter — floors whole dollars so this never disagrees with any other surface.
    const fuelBalance = formatFuelUsd(transactionsData?.balanceInUsd ?? 0, {cents: fuelShowCents});

    // LootShop cart count — persistent badge on the topnav cart icon
    const menuOrdersMap = useSelector((s: IStore) => s.metaStore.menuOrders);
    const lootShopMenuOrderId = menuOrdersMap?.[DEVOUR_MERCHANT_ID];
    const { data: lootShopMenuOrder } = useMenuOrder(lootShopMenuOrderId);
    const cartCount = lootShopMenuOrder?.orderItems?.reduce(
        (acc, item) => acc + (item.quantity ?? 0),
        0,
    ) ?? 0;

    /*
     * Active multipliers stack additively (sub 1.1x + lootbox 1.5x = 1.6x).
     * expiryEpoch re-runs the filter when the SHORTEST one lapses, so a
     * stacked user drops 1.6x → 1.5x at that moment instead of the whole
     * boost display vanishing until the next profile refetch.
     */
    const [expiryEpoch, setExpiryEpoch] = useState(0);
    const activeMultipliers = useMemo(() => {
        return userProfile?.user?.xpMultipliers?.filter((doc) => {
            return doc.expiresAt >= Date.now();
        }) || [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProfile?.user?.xpMultipliers, expiryEpoch]);

    /*
     * Total multiplier, rounded — floating point turns 1.1+1.5 into
     * "1.6000000000000001x" otherwise.
     */
    const multiplierAmount = useMemo(() => {
        const total = activeMultipliers.reduce((accumulated, doc) => {
            const boost = doc.amount - 1;
            return accumulated + boost;
        }, 1);
        return Math.round(total * 100) / 100;
    }, [activeMultipliers]);

    // Find the earliest expiration time
    const earliestExpiration = useMemo(() => {
        if (activeMultipliers.length === 0) return null;
        return Math.min(...activeMultipliers.map(m => m.expiresAt));
    }, [activeMultipliers]);

    // Countdown state
    const [secondsLeft, setSecondsLeft] = useState<number>(0);

    // Initialize and update countdown
    useEffect(() => {
        if (!earliestExpiration) {
            setSecondsLeft(0);
            return;
        }

        const updateCountdown = () => {
            const remaining = Math.max(0, Math.floor((earliestExpiration - Date.now()) / 1000));
            setSecondsLeft(remaining);
            /*
             * Shortest boost just lapsed — re-derive the active set so any
             * remaining multiplier keeps showing with its own countdown.
             */
            if (remaining === 0) setExpiryEpoch((e) => e + 1);
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [earliestExpiration]);

    const hasActiveMultiplier = multiplierAmount > 1 && secondsLeft > 0;
    const multiplierValue = `${multiplierAmount}x`;

    /*
     * Format seconds to "6d 23h" / "23h 48m" / "48m" / "13s" — readable
     * at a glance. Raw H:M:S reads like a clock and is confusing when
     * hours overflow (e.g. 167 hours of remaining boost time).
     */
    const formatCountdown = (seconds: number): string => {
        if (seconds <= 0) return "Expired";
        const days = Math.floor(seconds / 86_400);
        const hrs = Math.floor(seconds % 86_400 / 3_600);
        const mins = Math.floor(seconds % 3_600 / 60);
        const secs = Math.floor(seconds % 60);

        if (days > 0) return `${days}d ${hrs}h`;
        if (hrs > 0) return `${hrs}h ${mins}m`;
        if (mins > 0) return `${mins}m`;
        return `${secs}s`;
    };

    const isAuthenticated = !!fullToken;
    const showStats = !props.hideStats && isAuthenticated;

    /*
     * Get page title - used for aria/title only now that the section nav
     * owns the visible "where am I" affordance via its active state.
     */
    const pageTitle = props.text || getPageTitleFromRoute(location.pathname, location.search);

    /*
     * Section nav definition — 5 verb-oriented tabs: PLAY · DROP-IN · COMPETE
     * · STASH · LOOT. Order is locked left-to-right per the architecture
     * decision recorded in the gamification-mockup-derived topbar. Guilds
     * folds into Compete (still matched on /guilds + ?view=guilds so the
     * old links don't go dark). Rewards folds into Stash. Shop becomes Loot.
     *
     * Each entry has a `match` predicate against the current pathname so the
     * active state survives sub-routes.
     */
    /*
     * Each match predicate uses explicit parentheses around `&&` clusters
     * to lock operator precedence. JavaScript's `&&` binds tighter than
     * `||`, so the original form was correct — but the next edit that adds
     * another `||` term silently regresses. Parens make the intent
     * mechanical and edit-safe.
     */
    /*
     * Drop-In master killswitch — the tab is shown only once the killswitch
     * config has RESOLVED and isActive !== false. Using the resolved variant
     * (not the optimistic useGetDropInVisible) means the Drop-In tab no longer
     * flashes in then disappears on a disabled environment during page load.
     */
    const dropInReady = useGetDropInReady();

    const sectionNav: Array<{ label: string; href: string; match: (path: string) => boolean }> = [
        {
            label: "Play",
            href: "/play",
            match: (p) =>
                p === "/" ||
                p.startsWith("/play") &&
                    !location.search.includes("view=rewards") &&
                    !location.search.includes("view=guilds"),
        },
        ...dropInReady ? [{
            label: "Drop-In",
            href: "/drop-in",
            match: (p: string) => p.startsWith("/drop-in"),
        }] : [],
        {
            label: "Compete",
            href: "/competitions",
            match: (p) =>
                p.startsWith("/competitions") ||
                p.startsWith("/tournaments") ||
                p.startsWith("/ladders") ||
                p.startsWith("/challenges") ||
                p.startsWith("/challenge") ||
                p.startsWith("/leaderboards"),
        },
        {
            /*
             * Guilds is a first-class surface (design-guide anchor #06) but the
             * initial top-nav consolidation folded it into Compete WITHOUT a
             * link — so a user not already in a guild had no way to reach
             * /guilds and discover/join one. Restore it as its own tab; it owns
             * the /guilds + ?view=guilds match so Compete no longer claims it.
             */
            label: "Guilds",
            href: "/guilds",
            match: (p) =>
                p.startsWith("/guilds") ||
                p.startsWith("/play") && location.search.includes("view=guilds"),
        },
        {
            label: "Stash",
            href: "/stash",
            match: (p) =>
                p.startsWith("/stash") ||
                p.startsWith("/collectibles") ||
                p.startsWith("/rewards") ||
                p.startsWith("/play") && location.search.includes("view=rewards"),
        },
        {
            label: "Loot",
            href: "/lootshop",
            match: (p) =>
                p.startsWith("/lootshop") ||
                p.startsWith("/shop") ||
                p.startsWith("/loot"),
        },
    ];

    function handleToastDismissal() {
        setShowToast(false);
    }

    return (
        <>
            <SignUpToast
                message='Join DevourPlay and start your GoFriends journey!'
                isOpen={showToast}
                onDismiss={handleToastDismissal}
            />
            {/*
              * No season ribbon — a full-width strip for a line that changes
              * weekly was the most expensive chrome on the page for the least
              * volatile fact (BUILD_PLAN §11.3). The season week is the live
              * slot's lowest-priority entry instead.
              */}
            <div className="devour-top-nav">
                <div className="devour-top-nav_navbar">
                    {/* Add empty spacer when in Overwolf mode without logo to push user-data right */}
                    {isOnOverwolf && !props.logoUrl && !props.text && <div />}
                    {props.logoUrl && <img
                        className="devour-top-nav_navbar_logo"
                        src={props.logoUrl}
                        alt="GoFrens"
                    />}
                    {(!isOnOverwolf || isElectron) &&
                        <div className="devour-top-nav_navbar_title-section">
                            <button
                                type="button"
                                className="devour-top-nav_navbar_brand"
                                onClick={handleBrandClick}
                                aria-label="DevourPlay home"
                                title="DevourPlay"
                            >
                                <img
                                    className="devour-top-nav_navbar_brand-icon"
                                    src={DevourPlayIcon}
                                    alt="DevourPlay"
                                />
                            </button>
                            <nav className="devour-top-nav_navbar_section-nav" aria-label={`Section navigation — ${pageTitle}`}>
                                {sectionNav.map((item) => {
                                    const active = item.match(location.pathname);
                                    return (
                                        <button
                                            key={item.label}
                                            type="button"
                                            className={`devour-top-nav_navbar_section-nav_link${active ? " is-active" : ""}`}
                                            onClick={() => navigate(item.href)}
                                            aria-current={active ? "page" : undefined}
                                        >
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>
                    }

                    {/* Stats Section - Guild, Level, XP, Multiplier */}
                    {showStats &&
                        <div className="devour-top-nav_navbar_stats">
                            {/*
                              * One slot, highest-priority thing only. Replaces the
                              * guild badge, the gift button, the LVL badge, the
                              * Fuel-Up chip and the multiplier — five conditional
                              * chips whose combined width changed without warning,
                              * which is what forced the bar to wrap. Everything
                              * they carried moves to the player panel; the slot is
                              * a promotion, not the only door.
                              */}
                            <TopNavLiveSlot
                                state={{
                                    tracking: trackingState,
                                    xp: { level, gained: experienceGained, total: totalForLevel },
                                    boost: hasActiveMultiplier
                                        ? { value: multiplierValue, countdown: formatCountdown(secondsLeft) }
                                        : null,
                                    fuelUp: fuelUpResume,
                                    streakAtRisk: streakAtRiskState
                                        ? {
                                            days: streakAtRiskState.days,
                                            countdown: formatCountdown(Math.max(0, Math.floor((streakAtRiskState.endsAt - Date.now()) / 1000))),
                                        }
                                        : null,
                                    season: seasonRibbon
                                        ? {
                                            name: seasonRibbon.name,
                                            week: seasonRibbon.currentWeek,
                                            totalWeeks: seasonRibbon.totalWeeks,
                                        }
                                        : null,
                                }}
                            />
                        </div>
                    }

                    {/* Right Section - Fuel + User Data OR Login/Sign Up */}
                    <div className="devour-top-nav_navbar_right">
                        {isAuthenticated
                            ? <>
                                {/* Fuel Balance */}
                                {showStats &&
                                    <button
                                        className="devour-top-nav_navbar_stats_fuel"
                                        onClick={() => setShowLoadFuelModal(true)}
                                        aria-label="Load Fuel"
                                    >
                                        <img
                                            className="devour-top-nav_navbar_stats_fuel-icon"
                                            src={`${import.meta.env.VITE_CDN_URL}/images/FUEL.webp`}
                                            alt="Fuel"
                                        />
                                        <span className="devour-top-nav_navbar_stats_fuel-amount">{fuelBalance}</span>
                                    </button>
                                }

                                {/* Center Icons Container */}
                                {showStats &&
                                    <div className="devour-top-nav_navbar_center-icons">
                                        {/* Notifications - opens social drawer (only if any features enabled) */}
                                        {showSocialDrawerBell &&
                                            <button
                                                className="devour-top-nav_navbar_icon-btn devour-top-nav_navbar_icon-btn--notifications"
                                                onClick={() => openDrawer("activity")}
                                                aria-label="Alerts"
                                                title="Alerts"
                                            >
                                                <FaBell />
                                                {totalUnreadCount > 0 &&
                                                    <span className="devour-top-nav_navbar_notification-badge">
                                                        {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
                                                    </span>
                                                }
                                            </button>
                                        }

                                        {/* Cart - persistent across pages. On /lootshop, dispatches
                                            an event the page listens for to open its drawer. Off
                                            /lootshop, routes to the page (drawer auto-opens via the
                                            ?open=cart query param). */}
                                        {cartCount > 0 && <a
                                            className="devour-top-nav_navbar_icon-btn devour-top-nav_navbar_icon-btn--notifications"
                                            href="/lootshop?open=cart"
                                            aria-label={cartCount > 0 ? `LootShop cart, ${cartCount} items` : "LootShop cart"}
                                            title="LootShop"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (location.pathname === "/lootshop") {
                                                    window.dispatchEvent(new CustomEvent("lootshop:open-cart"));
                                                } else {
                                                    // Client-side route — the raw href would full-reload the app off /lootshop.
                                                    navigate("/lootshop?open=cart");
                                                }
                                            }}
                                        >
                                            <FaShoppingCart />
                                            <span className="devour-top-nav_navbar_notification-badge">
                                                {cartCount > 99 ? "99+" : cartCount}
                                            </span>
                                        </a>}

                                        {/* My Activity - opens activity drawer. Phone: panel row only. */}
                                        <button
                                            className="devour-top-nav_navbar_icon-btn devour-top-nav_navbar_icon-btn--activity"
                                            onClick={() => setShowActivityDrawer(true)}
                                            aria-label="My Activity"
                                            title="My Activity"
                                        >
                                            <FaChartLine />
                                        </button>

                                        {/*
                                          * Help lived here AND in the account dropdown
                                          * (the account dropdown's Help row), both
                                          * firing the identical toggleChatOpen(true). The
                                          * dropdown row is the one that stays — it sits with
                                          * the other account/support rows.
                                          */}
                                    </div>
                                }

                                <DevourTopNavUserData
                                    dropdownAnchorElement=".devour-top-nav"
                                    onShowSignInToast={() => setShowToast(true)}
                                    showGameStatus={isOnOverwolf}
                                    gameStatusCompact={false}
                                    /*
                                     * Top navbar shows the game-tracking status as a colored dot
                                     * ONLY (gray/green/yellow/red), never text — always icon-only,
                                     * not just on mobile. The full text chip (game name + degraded/
                                     * offline detail) lives on the game page; here the text pushed
                                     * the avatar off the right edge in the narrow in-game panel.
                                     * Hover tooltip still carries the detail.
                                     */
                                    gameStatusIconOnly={true}
                                    onOpenRewardsDrawer={() => setShowRewardsDrawer(true)}
                                    hideUserImage={props.hideUserImage}
                                    /*
                                     * Everything the bar used to carry as its own chip.
                                     * The data is already fetched here, so the panel is
                                     * handed values rather than fetching them again.
                                     */
                                    playerPanel={{
                                        xp: { level, gained: experienceGained, total: totalForLevel },
                                        boost: hasActiveMultiplier
                                            ? { value: multiplierValue, countdown: formatCountdown(secondsLeft) }
                                            : null,
                                        streakDays: aliveStreakCount,
                                        guild: currentGuild
                                            ? {
                                                name: currentGuild.name,
                                                rank: guildRank,
                                                yourRank: userRankInGuild,
                                                iconUrl: guildIconAsset?.url,
                                            }
                                            : null,
                                        /*
                                         * Whatever the live slot did not take. The slot
                                         * shows one thing; this is where the rest stay
                                         * reachable, with their verbs intact.
                                         */
                                        inProgress: [
                                            ...fuelUpResume
                                                ? [{
                                                    key: "fuelup",
                                                    label: "Fuel-Up",
                                                    value: !fuelUpResume.isActive
                                                        ? "Manage your vault"
                                                        : fuelUpResume.moves > 0
                                                            ? `Loop ${fuelUpResume.loop} · ${fuelUpResume.moves} Move${fuelUpResume.moves === 1 ? "" : "s"}`
                                                            : `Loop ${fuelUpResume.loop} · ${fuelUpResume.toNextMove ?? "no moves"}`,
                                                    verb: fuelUpResume.isActive && fuelUpResume.moves > 0 ? "Move" : undefined,
                                                    onClick: () => navigate("/fuel-up"),
                                                }]
                                                : [],
                                        ],
                                        onOpenLevelRewards: () => setShowLevelsModal(true),
                                        onOpenXpLeaderboard: () => setShowLeaderboardDrawer(true),
                                        onOpenMyActivity: () => setShowActivityDrawer(true),
                                    }}
                                />
                            </>
                            : <>
                                {/* Discover page link for unauthenticated users */}
                                <button
                                    className="devour-top-nav_navbar_icon-btn"
                                    onClick={() => navigate("/devourplay-discover")}
                                    aria-label="Discover DevourPlay"
                                    title="Discover DevourPlay"
                                >
                                    <FaRegLightbulb />
                                </button>

                                {/* Login / Sign Up buttons for unauthenticated users */}
                                <button
                                    className="devour-top-nav_navbar_auth-btn devour-top-nav_navbar_auth-btn--login"
                                    onClick={() => setShowLoginModal(true)}
                                >
                                    Log In
                                </button>
                                <button
                                    className="devour-top-nav_navbar_auth-btn devour-top-nav_navbar_auth-btn--signup"
                                    onClick={() => setShowLoginModal(true)}
                                >
                                    Sign Up
                                </button>
                            </>
                        }
                    </div>
                </div>

                {/*
                  * No dock and no More sheet here — DevourPlayMobileNav
                  * (PageWrapper-mounted) is the one bottom navigation, restyled
                  * to the anchor spec with its own More sheet.
                  */}
            </div>

            {/* Modals */}
            <LevelRewardsDrawer
                show={showLevelsModal}
                toggle={() => setShowLevelsModal(false)}
            />
            <LeaderboardDrawer
                show={showLeaderboardDrawer}
                toggle={() => setShowLeaderboardDrawer(false)}
            />
            <LoadFuelModal
                isOpen={showLoadFuelModal}
                toggle={() => setShowLoadFuelModal(false)}
                size="xs2"
            />

            {/* Login Modal */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
            />

            {/* My Activity Drawer */}
            <MyActivityDrawer
                show={showActivityDrawer}
                toggle={() => setShowActivityDrawer(false)}
            />

            {/* My Rewards Drawer */}
            <GoVipRewardsInventoryDrawer
                show={showRewardsDrawer}
                toggle={() => setShowRewardsDrawer(false)}
            />
        </>
    );
}

export default DevourTopNav;
